import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Clock, Home } from 'lucide-react';
import { ArticleBody, ArticleCta, ArticleFaq, RelatedArticles, SiloBadge } from '../../components/Blog';
import { getArticleBySlug, getRelatedArticles } from '../../data/blog';
import { SILOS } from '../../data/blog/silos';

const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    if (!article) return;

    document.title = article.metaTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', article.metaDescription);
    setMeta('meta[property="og:title"]', 'content', article.metaTitle);
    setMeta('meta[property="og:description"]', 'content', article.metaDescription);
    setMeta('meta[property="og:url"]', 'content', `https://sos-it-services.fr/blog/${article.slug}`);
    setMeta(
      'meta[property="og:image"]',
      'content',
      `https://sos-it-services.fr${article.coverImage ?? '/images/blog-hero.png'}`,
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://sos-it-services.fr/blog/${article.slug}`);

    const existingLd = document.getElementById('article-ld-json');
    if (existingLd) existingLd.remove();
    const script = document.createElement('script');
    script.id = 'article-ld-json';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.h1,
      description: article.metaDescription,
      datePublished: article.publishedAt,
      author: { '@type': 'Organization', name: 'SOS IT Services' },
      publisher: {
        '@type': 'Organization',
        name: 'SOS IT Services',
        url: 'https://sos-it-services.fr',
      },
      url: `https://sos-it-services.fr/blog/${article.slug}`,
    });
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      document.getElementById('article-ld-json')?.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const silo = SILOS[article.silo];
  const related = getRelatedArticles(article);
  const coverImage = article.coverImage ?? '/images/blog-hero.png';
  const coverAlt = article.coverImageAlt ?? `Illustration de l'article : ${article.h1}`;

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#2F3136]">
      {/* Breadcrumb */}
      <div className="bg-[#36393F] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 flex-wrap">
            <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
              <Home className="h-3.5 w-3.5" /> Accueil
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to={`/blog/silo/${article.silo}`} className="hover:text-white transition-colors">
              {silo.title}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-gray-300 line-clamp-1">{article.h1}</span>
          </nav>
        </div>
      </div>

      {/* Header article */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-[#36393F] to-[#2F3136]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <div className="flex items-center gap-3 mb-5">
              <SiloBadge silo={article.silo} />
              {article.isPillar && (
                <span className="rounded-full border border-[#5865F2]/35 bg-[#5865F2]/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B9C4FF]">
                  Guide complet
                </span>
              )}
              <span className="flex items-center gap-1.5 text-gray-500 text-xs ml-auto">
                <Clock className="h-3.5 w-3.5" />
                {article.readingMinutes} min de lecture
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-4">
              {article.h1}
            </h1>

            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              {article.excerpt}
            </p>

            <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 shadow-lg shadow-black/20">
              <img src={coverImage} alt={coverAlt} className="h-auto w-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-[#5865F2]/25 to-transparent" />

      {/* Corps de l'article */}
      <section className="py-12 bg-[#2F3136]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          >
            <ArticleBody content={article.content} />

            <div className="mt-10">
              <ArticleCta variant="inline" />
            </div>

            <ArticleFaq faq={article.faq} />

            <RelatedArticles articles={related} />

            <div className="mt-12">
              <ArticleCta variant="inline" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticle;
